import { Head, router } from "@inertiajs/react"
import { FileText, Clock, CheckCircle, AlertCircle, Plus, Search } from "lucide-react"
import { useState } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { SectionTabs } from "../components/SectionTabs"

const articlesTabs = [
  { label: "Articles", href: "/app/coach/articles" },
  { label: "Meetings", href: "/app/coach/meetings" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { NewArticleChooserDialog } from "@/components/NewArticleChooserDialog"
import { UploadTranscriptDialog } from "@/components/UploadTranscriptDialog"

const statusConfig = {
  draft: { label: "Draft", variant: "secondary", icon: FileText },
  pending_review: { label: "Pending Review", variant: "warning", icon: Clock },
  published: { label: "Published", variant: "success", icon: CheckCircle },
  rejected: { label: "Rejected", variant: "destructive", icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.draft
  return (
    <Badge
      variant={config.variant}
      className={
        config.variant === "warning"
          ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
          : config.variant === "success"
          ? "bg-green-100 text-green-800 hover:bg-green-100"
          : ""
      }
    >
      {config.label}
    </Badge>
  )
}

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const statusFilters = [
  { key: null, label: "All", statKey: "total" },
  { key: "draft", label: "Drafts", statKey: "drafts" },
  { key: "pending_review", label: "Pending", statKey: "pendingReview" },
  { key: "published", label: "Published", statKey: "published" },
  { key: "rejected", label: "Rejected", statKey: "rejected" },
]

export default function ArticlesIndex({
  currentUser,
  articles,
  stats,
  categories,
  currentStatus,
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [dialogMode, setDialogMode] = useState(null) // null | "chooser" | "upload"

  const handleNewArticle = () => {
    setDialogMode("chooser")
  }

  const handleFilterChange = (status) => {
    const params = status ? { status } : {}
    router.get("/app/coach/articles", params, { preserveState: true })
  }

  const filteredArticles = articles?.filter((article) =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="My Articles - Kingdom Factor" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Articles</h1>
            <p className="text-zinc-600">Manage your article submissions and meetings</p>
          </div>
          <Button variant="gradient" data-tour="new-article-btn" onClick={handleNewArticle}>
            <Plus className="h-4 w-4" />
            New Article
          </Button>
        </div>

        <SectionTabs tabs={articlesTabs} />

        {/* Stats / Status Filters */}
        <div data-tour="status-filters" className="grid gap-4 grid-cols-2 lg:grid-cols-5">
          {statusFilters.map((filter) => {
            const count = stats?.[filter.statKey] || 0
            const isActive = currentStatus === filter.key
            return (
              <button
                key={filter.label}
                onClick={() => handleFilterChange(filter.key)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  isActive
                    ? "border-kf-red bg-kf-red/5"
                    : "border-zinc-200 hover:border-zinc-300 bg-white"
                }`}
              >
                <p className="text-sm text-zinc-600">{filter.label}</p>
                <p className="text-2xl font-bold">{count}</p>
              </button>
            )
          })}
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            type="search"
            placeholder="Search articles..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Articles Table */}
        <Card data-tour="articles-table">
          <CardContent className="p-0">
            {filteredArticles?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredArticles.map((article) => (
                    <TableRow
                      key={article.id}
                      className="cursor-pointer"
                      onClick={() =>
                        router.visit(`/app/coach/articles/${article.id}`)
                      }
                    >
                      <TableCell className="font-medium">
                        {article.title}
                        {article.rejectionReason && (
                          <p className="text-xs text-red-600 mt-1">
                            Rejection: {article.rejectionReason}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-600">
                        {article.category || "-"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={article.status} />
                      </TableCell>
                      <TableCell className="text-zinc-600">
                        {formatDate(article.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium">No articles found</p>
                <p className="text-sm">
                  {searchQuery
                    ? "Try a different search term"
                    : "Create your first article to get started"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <NewArticleChooserDialog
        open={dialogMode === "chooser"}
        // Functional updater prevents Radix/Vaul's onOpenChange(false) from
        // resetting dialogMode when transitioning to "upload" — the close
        // callback fires after dialogMode has already moved to "upload".
        onOpenChange={(open) => { if (!open) setDialogMode((m) => m === "chooser" ? null : m) }}
        onStartScratch={() => {
          setDialogMode(null)
          router.post("/app/coach/articles")
        }}
        onUploadTranscript={() => setDialogMode("upload")}
      />
      <UploadTranscriptDialog
        open={dialogMode === "upload"}
        onOpenChange={(open) => { if (!open) setDialogMode(null) }}
      />
    </CoachLayout>
  )
}
